import type { FeatureShellPorts } from '@src/core/bootstrap/FeatureShellPorts.ts';
import {
  noteStartupCatalogSyncOutcome,
  shouldRetryCatalogDownloadAfterStartup,
  type StartupCatalogSyncOutcome,
} from '@src/modules/sync/application/startupCatalogSyncOutcome.ts';
import { buildCatalogDownloadOutcomeNotification } from '@src/modules/sync/infrastructure/drivers/page/catalogDownloadSuccessMessage.ts';
import { acknowledgeSnapshotRevisionCatalogRefresh } from './postSnapshotRevisionUpgradeNotify.ts';
import { runPostAuthCatalogSyncOverlay } from './startupSyncOverlay.ts';

let retryInFlight: Promise<void> | null = null;

/**
 * After session restore or login: retry startup catalog download when cold boot failed
 * (e.g. HTTP 401 before JWT was available, timeout, or offline).
 */
export function retryPostAuthCatalogDownloadIfNeeded(
  shellPorts: FeatureShellPorts,
  startupOutcome: StartupCatalogSyncOutcome | null,
): Promise<void> {
  if (!shouldRetryCatalogDownloadAfterStartup(startupOutcome)) {
    return Promise.resolve();
  }
  if (shellPorts.catalogDownload.resolveDownloadUrl() == null) {
    return Promise.resolve();
  }
  if (retryInFlight != null) return retryInFlight;

  retryInFlight = runRetry(shellPorts, startupOutcome).finally(() => {
    retryInFlight = null;
  });
  return retryInFlight;
}

async function runRetry(
  shellPorts: FeatureShellPorts,
  startupOutcome: StartupCatalogSyncOutcome,
): Promise<void> {
  console.info('[StartupSync] Retrying catalog download after session is available.', startupOutcome);
  const outcome = await runPostAuthCatalogSyncOverlay(shellPorts.catalogDownload);
  if (outcome.isFailure) {
    console.warn('[StartupSync] Post-auth catalog retry failed:', outcome.error);
    shellPorts.notifications.warning(
      'No se pudieron actualizar los catálogos tras iniciar sesión. Use Sync → Descargar ahora cuando haya red.',
    );
    return;
  }

  const notification = buildCatalogDownloadOutcomeNotification(outcome.value);
  if (notification.level === 'success') {
    noteStartupCatalogSyncOutcome({ status: 'applied' });
    acknowledgeSnapshotRevisionCatalogRefresh();
    shellPorts.notifications.success(`Tras iniciar sesión: ${notification.message}`);
  } else if (notification.level === 'info') {
    noteStartupCatalogSyncOutcome({ status: 'skipped_revision' });
    shellPorts.notifications.info(notification.message);
  } else {
    shellPorts.notifications.warning(notification.message);
  }
}
